import { NavigateFunction } from 'react-router-dom';

/**
 * Shown when the API's rate limiter rejects a request (HTTP 429). Worth saying plainly
 * rather than reporting a generic failure: the limit is per IP per minute, so the work was
 * not rejected on its merits and waiting is the whole fix. Generating or downloading
 * certificates one by one for a large insured is enough to reach it.
 */
export const RATE_LIMITED = 'Too many requests in a short time. Wait about a minute and try again.';

export async function handleApiResponse(res: Response, navigate?: NavigateFunction) {
  if (res.status === 401) {
    window.localStorage.clear();
    if (navigate) navigate('/');
    throw new Error('Unauthorized');
  }
  // Checked before the server's own message, because the rate limiter answers with an empty
  // body and sits outside the controllers — so there is nothing to quote, and falling through
  // would report a throttle as a generic failure. It is capped per IP per minute, so the
  // caller has not done anything wrong and the same request will work shortly.
  if (res.status === 429) throw new Error(RATE_LIMITED);

  if (!res.ok) {
    let serverMsg = '';
    try {
      const body = await res.clone().json();
      serverMsg = body?.message || body?.error || body?.title || '';
    } catch {}
    if (serverMsg) throw new Error(serverMsg);
    if (res.status >= 500) throw new Error('__server_error__');
    if (res.status === 400) throw new Error('__bad_request__');
    throw new Error('__api_error__');
  }
  return res;
}

export function handleAxiosError(err: any, navigate?: NavigateFunction) {
  if (err && err.response && err.response.status === 401) {
    window.localStorage.clear();
    if (navigate) navigate('/');
    throw new Error('Unauthorized');
  }
  if (err?.response?.status === 429) throw new Error(RATE_LIMITED);
  throw err;
}
